import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { loadGuestTrainerSession, saveGuestTrainerSession } from '../utils/guestPractice';
import TrainerAdjTable from '../components/trainer/TrainerAdjTable';
import TrainerCodeTable from '../components/trainer/TrainerCodeTable';
import TrainerFanoTree from '../components/trainer/TrainerFanoTree';
import TrainerGraphSvg from '../components/trainer/TrainerGraphSvg';
import TrainerIpBits, {
  TrainerIpDecimal,
  normalizeIpBitsAnswer,
} from '../components/trainer/TrainerIpBits';
import TrainerPlaneSvg from '../components/trainer/TrainerPlaneSvg';
import TrainerSliceRuler from '../components/trainer/TrainerSliceRuler';

function TextBlock({ texts }) {
  if (!texts?.length) return null;
  return (
    <div style={{ marginTop: '0.75rem' }}>
      <div className="text-secondary" style={{ marginBottom: '0.35rem' }}>
        Строки для проверки (findall):
      </div>
      <ul style={{ margin: 0, paddingLeft: '1.2rem', fontFamily: 'ui-monospace, monospace' }}>
        {texts.map((t) => (
          <li key={t}>
            <code>{JSON.stringify(t)}</code>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Plane15Options({
  prompt,
  selectedId,
  onSelect,
  name,
  wrongIds = [],
  correctFoundId = '',
  locked = false,
}) {
  const opts = prompt?.options || [];
  if (!opts.length) return null;
  return (
    <div className="trainer-graph-options">
      {opts.map((opt) => {
        const isWrong = wrongIds.includes(opt.id);
        const isCorrectFound = correctFoundId === opt.id;
        return (
          <label
            key={opt.id}
            className={[
              'trainer-graph-option',
              selectedId === opt.id ? 'is-selected' : '',
              isWrong ? 'is-wrong' : '',
              isCorrectFound ? 'is-correct-found' : '',
            ].filter(Boolean).join(' ')}
          >
            <input
              type="radio"
              name={name}
              checked={selectedId === opt.id}
              disabled={locked || isWrong}
              onChange={() => onSelect(opt.id)}
            />
            <span className="trainer-graph-option-body">
              <TrainerPlaneSvg plane={opt.content} compact />
            </span>
          </label>
        );
      })}
    </div>
  );
}

function Graph1Options({
  prompt,
  selectedId,
  onSelect,
  name,
  wrongIds = [],
  correctFoundId = '',
  locked = false,
}) {
  const opts = prompt?.options || [];
  if (!opts.length) return null;
  const isTableOpts = prompt.type === 'graph_to_table';
  return (
    <div className="trainer-graph-options">
      {opts.map((opt) => {
        const isWrong = wrongIds.includes(opt.id);
        const isCorrectFound = correctFoundId === opt.id;
        return (
          <label
            key={opt.id}
            className={[
              'trainer-graph-option',
              selectedId === opt.id ? 'is-selected' : '',
              isWrong ? 'is-wrong' : '',
              isCorrectFound ? 'is-correct-found' : '',
            ].filter(Boolean).join(' ')}
          >
            <input
              type="radio"
              name={name}
              checked={selectedId === opt.id}
              disabled={locked || isWrong}
              onChange={() => onSelect(opt.id)}
            />
            <span className="trainer-graph-option-body">
              {isTableOpts ? (
                <TrainerAdjTable table={opt.content} compact />
              ) : (
                <TrainerGraphSvg graph={opt.content} compact />
              )}
            </span>
          </label>
        );
      })}
    </div>
  );
}

function McqTextOptions({
  prompt,
  selectedId,
  onSelect,
  name,
  wrongIds = [],
  correctFoundId = '',
  locked = false,
}) {
  const twoCol = prompt?.type === 'choose_tool';
  return (
    <div
      className={twoCol ? 'trainer-ege8pp-options' : undefined}
      style={twoCol ? undefined : { marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '0.4rem' }}
    >
      {(prompt?.options || []).map((opt) => {
        const isWrong = wrongIds.includes(opt.id);
        const isCorrectFound = correctFoundId === opt.id;
        return (
          <label
            key={opt.id}
            className={[
              'checkbox-row',
              'trainer-mcq-option',
              isWrong ? 'is-wrong' : '',
              isCorrectFound ? 'is-correct-found' : '',
            ].filter(Boolean).join(' ')}
            style={{ display: 'flex', gap: '0.5rem' }}
          >
            <input
              type="radio"
              name={name}
              checked={selectedId === opt.id}
              disabled={locked || isWrong}
              onChange={() => onSelect(opt.id)}
            />
            <span
              style={{
                fontFamily:
                  prompt?.type === 'choose_desc'
                  || prompt?.type === 'same_net'
                  || prompt?.type === 'mask_ok'
                  || prompt?.type === 'addr_kind'
                    ? 'inherit'
                    : 'ui-monospace, monospace',
              }}
            >
              {opt.text}
            </span>
          </label>
        );
      })}
    </div>
  );
}

function isTextAnswerType(type) {
  return (
    type === 'write_regex'
    || type === 'write_logic'
    || type === 'number_answer'
    || type === 'find_vertex'
    || type === 'write_code'
    || type === 'write_letter'
    || type === 'bit_answer'
    || type === 'predict_result'
    || type === 'write_slice'
  );
}

function isBitAnswerReady(answer) {
  return Boolean(normalizeIpBitsAnswer(answer));
}

function ip10DisplayRows(prompt) {
  const rows = [...(prompt?.rows || [])];
  if (prompt?.type === 'bit_answer' && prompt.answer_label) {
    const exists = rows.some((r) => r.label === prompt.answer_label);
    if (!exists) {
      rows.push({ label: prompt.answer_label, bits: '' });
    }
  }
  return rows;
}

function isReadingRecall(prompt) {
  return prompt?.type === 'reading_recall' || prompt?.kind === 'reading';
}

function readingAnswersReady(prompt, answers) {
  const qs = prompt?.questions || [];
  if (!qs.length) return false;
  return qs.every((q) => Boolean(answers?.[q.id]));
}

function isMcqPrompt(prompt) {
  return [
    'graph_to_table',
    'table_to_graph',
    'choose_regex',
    'choose_desc',
    'choose_tool',
    'which_rule',
    'line_dir',
    'line_signs',
    'side',
    'region',
    'same_net',
    'mask_ok',
    'addr_kind',
  ].includes(prompt?.type);
}

function StatementLines({ statement }) {
  const lines = (statement || '').split('\n');
  return (
    <div className="trainer-ege8pp-statement trainer-reading-statement">
      {lines.map((line, i) => (
        <p key={i} style={{ margin: line ? '0 0 0.35rem' : '0 0 0.55rem' }}>
          {line || '\u00a0'}
        </p>
      ))}
    </div>
  );
}

function ReadingQuestions({
  prompt,
  answers,
  onSelect,
  namePrefix,
  locked = false,
  perQuestion = null,
  showCorrect = false,
}) {
  const qs = prompt?.questions || [];
  if (!qs.length) return null;
  return (
    <div className="trainer-reading-questions">
      {qs.map((q, qi) => {
        const pq = perQuestion?.[q.id];
        const correctId = pq?.correct_id || '';
        const selected = answers?.[q.id] || '';
        return (
          <div key={q.id} className="trainer-reading-q">
            <p className="trainer-reading-q-text">
              {qi + 1}. {q.text}
            </p>
            <div className="trainer-reading-q-options">
              {(q.options || []).map((opt) => {
                const isWrong = Boolean(
                  pq && pq.ok === false && selected === opt.id && !showCorrect,
                );
                const isCorrectFound = Boolean(
                  showCorrect && correctId && opt.id === correctId,
                );
                return (
                  <label
                    key={opt.id}
                    className={[
                      'checkbox-row',
                      'trainer-mcq-option',
                      isWrong ? 'is-wrong' : '',
                      isCorrectFound ? 'is-correct-found' : '',
                    ].filter(Boolean).join(' ')}
                    style={{ display: 'flex', gap: '0.5rem' }}
                  >
                    <input
                      type="radio"
                      name={`${namePrefix}-${q.id}`}
                      checked={selected === opt.id}
                      disabled={locked}
                      onChange={() => onSelect(q.id, opt.id)}
                    />
                    <span>{opt.text}</span>
                  </label>
                );
              })}
            </div>
            {showCorrect && pq?.explanation && (
              <p className="text-secondary trainer-reading-q-explain">{pq.explanation}</p>
            )}
          </div>
        );
      })}
    </div>
  );
}

function isPlaneSvgPrompt(prompt) {
  return prompt?.kind === 'plane15' && (prompt.type === 'side' || prompt.type === 'region');
}

function McqPromptOptions(props) {
  const { prompt } = props;
  if (!isMcqPrompt(prompt)) return null;
  if (prompt?.kind === 'graph1') {
    return (
      <div style={{ marginTop: '1rem' }}>
        <Graph1Options {...props} />
      </div>
    );
  }
  if (isPlaneSvgPrompt(prompt)) {
    return (
      <div style={{ marginTop: '1rem' }}>
        <Plane15Options {...props} />
      </div>
    );
  }
  return <McqTextOptions {...props} />;
}

function ProblemPrompt({ prompt }) {
  if (!prompt) return null;
  if (prompt.type === 'write_logic' || prompt.kind === 'logic2') {
    return (
      <div>
        <p style={{ marginBottom: '0.5rem' }}>{prompt.instruction}</p>
        <p
          style={{
            margin: '0.75rem 0',
            fontSize: '1.35rem',
            fontFamily: 'ui-monospace, monospace',
            letterSpacing: '0.02em',
          }}
        >
          <code>{prompt.formula}</code>
        </p>
        {prompt.hint && (
          <p className="text-secondary" style={{ marginBottom: 0 }}>
            {prompt.hint}
          </p>
        )}
      </div>
    );
  }
  if (prompt.kind === 'plane15') {
    return (
      <div className="trainer-plane15-prompt">
        <p className="text-secondary" style={{ marginBottom: '0.35rem' }}>
          {prompt.mode_label}
        </p>
        <p style={{ marginBottom: '0.75rem' }}>{prompt.instruction}</p>
        {prompt.formula && prompt.type !== 'side' && (
          <p className="trainer-plane15-formula">
            <code>{prompt.formula}</code>
          </p>
        )}
        {prompt.plane && (
          <div className="trainer-graph1-stimulus">
            <TrainerPlaneSvg plane={prompt.plane} />
          </div>
        )}
      </div>
    );
  }
  if (prompt.kind === 'graph1') {
    return (
      <div className="trainer-graph1-prompt">
        <p className="text-secondary" style={{ marginBottom: '0.35rem' }}>
          {prompt.mode_label}
        </p>
        <p style={{ marginBottom: '0.75rem' }}>{prompt.instruction}</p>
        {prompt.type === 'graph_to_table' && prompt.graph && (
          <div className="trainer-graph1-stimulus">
            <TrainerGraphSvg graph={prompt.graph} />
          </div>
        )}
        {prompt.type === 'table_to_graph' && prompt.table && (
          <div className="trainer-graph1-stimulus">
            <TrainerAdjTable table={prompt.table} />
          </div>
        )}
        {prompt.type === 'find_vertex' && (
          <div className="trainer-graph1-find">
            {prompt.table && <TrainerAdjTable table={prompt.table} />}
            {prompt.graph && <TrainerGraphSvg graph={prompt.graph} />}
          </div>
        )}
      </div>
    );
  }
  if (prompt.kind === 'ege8pp') {
    const lines = (prompt.statement || '').split('\n');
    return (
      <div className="trainer-ege8pp-prompt">
        <p className="text-secondary" style={{ marginBottom: '0.35rem' }}>
          {prompt.topic_label}
        </p>
        <div className="trainer-ege8pp-statement">
          {lines.map((line, i) => (
            <p key={i} style={{ margin: line ? '0 0 0.35rem' : '0 0 0.55rem' }}>
              {line || '\u00a0'}
            </p>
          ))}
        </div>
        <p style={{ marginBottom: 0 }}>{prompt.instruction}</p>
      </div>
    );
  }
  if (prompt.kind === 'reading' || prompt.type === 'reading_recall') {
    return (
      <div className="trainer-reading-prompt">
        <p className="text-secondary" style={{ marginBottom: '0.35rem' }}>
          ЕГЭ-{prompt.ege_num || 3}
          {prompt.difficulty_label ? ` · ${prompt.difficulty_label}` : ''}
        </p>
        {prompt._showStatement !== false && (
          <StatementLines statement={prompt.statement} />
        )}
        {prompt._showStatement === false && (
          <p className="trainer-reading-hidden" role="status">
            Текст условия скрыт. Ответьте по памяти.
          </p>
        )}
        <p style={{ marginBottom: 0 }}>{prompt.instruction}</p>
      </div>
    );
  }
  if (prompt.kind === 'fano4') {
    return (
      <div className="trainer-fano4-prompt">
        <p className="text-secondary" style={{ marginBottom: '0.35rem' }}>
          {prompt.mode_label}
        </p>
        <p style={{ marginBottom: '0.75rem' }}>{prompt.instruction}</p>
        <div className="trainer-fano4-stimulus">
          {prompt.tree && (
            <div className="trainer-fano4-tree-wrap">
              <TrainerFanoTree tree={prompt.tree} />
            </div>
          )}
          {prompt.table && (
            <div className="trainer-fano4-table-wrap">
              <TrainerCodeTable table={prompt.table} />
            </div>
          )}
        </div>
      </div>
    );
  }
  if (prompt.kind === 'ip10') {
    return (
      <div className="trainer-ip10-prompt">
        <p className="text-secondary" style={{ marginBottom: '0.35rem' }}>
          {prompt.topic_label}
        </p>
        <p style={{ marginBottom: '0.75rem' }}>{prompt.instruction}</p>
        {prompt.type === 'same_net' && prompt.decimal_rows?.length > 0 && (
          <TrainerIpDecimal rows={prompt.decimal_rows} />
        )}
        {/* bit_answer: сетка с полем ответа рисуется отдельно, бит над битом */}
        {prompt.type !== 'bit_answer' && prompt.rows?.length > 0 && (
          <TrainerIpBits rows={prompt.rows} />
        )}
      </div>
    );
  }
  if (prompt.kind === 'slices') {
    const targetDisplay =
      prompt.target === undefined || prompt.target === null
        ? null
        : JSON.stringify(prompt.target);
    return (
      <div className="trainer-slices-prompt">
        <p className="text-secondary" style={{ marginBottom: '0.35rem' }}>
          {prompt.topic_label}
          {prompt.mode_label ? ` · ${prompt.mode_label}` : ''}
        </p>
        <p style={{ marginBottom: '0.75rem' }}>{prompt.instruction}</p>
        <div className="trainer-slices-stimulus">
          <div className="text-secondary" style={{ marginBottom: '0.35rem', fontSize: '0.9rem' }}>
            s =
          </div>
          <TrainerSliceRuler text={prompt.source || ''} showNegative={Boolean(prompt.show_negative)} />
        </div>
        {prompt.type === 'predict_result' && prompt.slice_expr && (
          <p className="trainer-slices-expr" style={{ marginTop: '0.85rem', marginBottom: 0 }}>
            Вычислите:{' '}
            <code>{prompt.slice_expr}</code>
          </p>
        )}
        {prompt.type === 'write_slice' && targetDisplay !== null && (
          <p className="trainer-slices-expr" style={{ marginTop: '0.85rem', marginBottom: 0 }}>
            Нужный результат:{' '}
            <code>{targetDisplay}</code>
          </p>
        )}
      </div>
    );
  }
  return (
    <div>
      <p className="text-secondary" style={{ marginBottom: '0.35rem' }}>
        {prompt.topic_label}
        {prompt.mode_label ? ` · ${prompt.mode_label}` : ''}
      </p>
      <p style={{ marginBottom: '0.5rem' }}>{prompt.instruction}</p>
      {prompt.description && (
        <p>
          <strong>Нужно найти:</strong> {prompt.description}
        </p>
      )}
      {prompt.pattern && (
        <p>
          <strong>Регулярка:</strong>{' '}
          <code style={{ fontSize: '1.05em' }}>{prompt.pattern}</code>
        </p>
      )}
      <TextBlock texts={prompt.texts} />
    </div>
  );
}

function ImmediateFeedback({ result }) {
  if (!result) return null;
  const ok = result.is_correct === true;
  const fb = result.feedback || {};
  const skipLongExplain = Boolean(fb.per_question);
  const bitView = fb.bit_view;
  return (
    <div
      className={ok ? 'alert alert-success' : 'alert alert-error'}
      style={{ marginTop: '1rem' }}
      role="status"
    >
      <p style={{ margin: 0, fontWeight: 600 }}>
        {fb.message || (ok ? 'Верно' : 'Неверно')}
      </p>
      {fb.explanation && !skipLongExplain && (
        <p style={{ margin: '0.5rem 0 0' }}>{fb.explanation}</p>
      )}
      {!fb.explanation && fb.pattern && (
        <p style={{ margin: '0.5rem 0 0' }}>
          Шаблон: <code>{fb.pattern}</code>
          {fb.description ? ` — ${fb.description}` : ''}
        </p>
      )}
      {bitView?.rows?.length > 0 && (
        <div style={{ marginTop: '0.75rem' }}>
          <TrainerIpBits
            rows={bitView.rows}
            highlight={bitView.highlight || []}
            locked
          />
        </div>
      )}
    </div>
  );
}

export default function TrainerSessionPage() {
  const { sessionId } = useParams();
  const { user } = useAuth();
  const isGuest = sessionId === 'guest';
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [commentDrafts, setCommentDrafts] = useState({});
  const [commentBusyId, setCommentBusyId] = useState(null);
  /** Результат только что отправленного ответа — покажем до «Далее». */
  const [lastResult, setLastResult] = useState(null);
  const [reviewPrompt, setReviewPrompt] = useState(null);
  /** Неверные варианты MCQ (остаются красными); локальный поиск верного без повторного submit. */
  const [wrongOptionIds, setWrongOptionIds] = useState([]);
  const [practiceSelectedId, setPracticeSelectedId] = useState('');
  const [practiceResolved, setPracticeResolved] = useState(false);
  /** reading_recall: подтверждение «прочитал» и ответы по вопросам. */
  const [readingConfirmed, setReadingConfirmed] = useState(false);
  const [readingAnswers, setReadingAnswers] = useState({});

  const load = useCallback(async ({ quiet } = {}) => {
    if (!quiet) setLoading(true);
    setError('');
    try {
      if (isGuest) {
        const local = loadGuestTrainerSession();
        if (!local) {
          setError('Гостевая сессия не найдена. Начните тренажёр заново.');
          setSession(null);
        } else {
          setSession(local);
        }
        return;
      }
      const res = await api.get(`/trainers/sessions/${sessionId}/`);
      setSession(res.data);
    } catch {
      setError('Не удалось загрузить сессию.');
      setSession(null);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [sessionId, isGuest]);

  useEffect(() => {
    void load();
  }, [load]);

  const problems = session?.problems || [];
  const current = useMemo(
    () => (session?.status === 'active' ? problems.find((p) => !p.checked_at) || null : null),
    [problems, session?.status],
  );
  const isFinished = session?.status === 'finished';
  const isOwner = Boolean(isGuest || (user && session && user.id === session.student));
  const teacherView = Boolean(
    user
    && session
    && user.id !== session.student
    && (user.is_teacher || user.is_staff || user.is_admin),
  );
  const allAnswered = problems.length > 0 && problems.every((p) => p.checked_at);
  const showingFeedback = Boolean(lastResult);

  useEffect(() => {
    if (showingFeedback) return;
    setAnswer('');
    setSelectedId('');
    setWrongOptionIds([]);
    setPracticeSelectedId('');
    setPracticeResolved(false);
    setReadingConfirmed(false);
    setReadingAnswers({});
  }, [current?.id, showingFeedback]);

  async function submitCurrent() {
    if (!current || showingFeedback) return;
    setBusy(true);
    setError('');
    try {
      let body;
      if (isReadingRecall(current.prompt)) {
        body = { answer: readingAnswers };
      } else if (current.prompt?.type === 'bit_answer') {
        body = { answer: normalizeIpBitsAnswer(answer) || answer.replace(/[^01]/g, '') };
      } else if (isTextAnswerType(current.prompt?.type)) {
        body = {
          answer: current.prompt?.type === 'number_answer' ? answer.trim() : answer,
        };
      } else {
        body = { answer: selectedId };
      }
      let res;
      if (isGuest) {
        res = await api.post('/trainers/guest/check/', {
          token: current.check_token,
          ...body,
        });
        const checkedAt = res.data.checked_at || new Date().toISOString();
        const updatedProblem = {
          ...current,
          student_answer: res.data.student_answer,
          is_correct: res.data.is_correct,
          feedback: res.data.feedback,
          checked_at: checkedAt,
        };
        const nextProblems = problems.map((p) => (p.id === current.id ? updatedProblem : p));
        const nextSession = {
          ...session,
          problems: nextProblems,
          problems_answered: nextProblems.filter((p) => p.checked_at).length,
          problems_correct: nextProblems.filter((p) => p.is_correct === true).length,
        };
        saveGuestTrainerSession(nextSession);
        setSession(nextSession);
        setReviewPrompt(current.prompt);
        setLastResult({ ...updatedProblem, ...res.data });
      } else {
        res = await api.post(`/trainers/problems/${current.id}/submit/`, body);
        setReviewPrompt(current.prompt);
        setLastResult(res.data);
      }
      if (
        res.data.is_correct !== true
        && isMcqPrompt(current.prompt)
        && !isReadingRecall(current.prompt)
      ) {
        setWrongOptionIds([selectedId]);
        setPracticeSelectedId('');
        setPracticeResolved(false);
      } else {
        setWrongOptionIds([]);
        setPracticeSelectedId('');
        setPracticeResolved(false);
      }
      await load({ quiet: true });
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось отправить ответ.');
    } finally {
      setBusy(false);
    }
  }

  function checkPracticeChoice() {
    const correctId = lastResult?.feedback?.correct_id;
    if (!practiceSelectedId || !correctId) return;
    if (practiceSelectedId === correctId) {
      setPracticeResolved(true);
      return;
    }
    setWrongOptionIds((prev) => (
      prev.includes(practiceSelectedId) ? prev : [...prev, practiceSelectedId]
    ));
    setPracticeSelectedId('');
  }

  function goNext() {
    setLastResult(null);
    setReviewPrompt(null);
    setAnswer('');
    setSelectedId('');
    setWrongOptionIds([]);
    setPracticeSelectedId('');
    setPracticeResolved(false);
    setReadingConfirmed(false);
    setReadingAnswers({});
  }

  async function finish() {
    setBusy(true);
    setError('');
    try {
      if (isGuest) {
        const nextSession = {
          ...(session || {}),
          status: 'finished',
          finished_at: new Date().toISOString(),
        };
        saveGuestTrainerSession(nextSession);
        setSession(nextSession);
      } else {
        await api.post(`/trainers/sessions/${sessionId}/finish/`);
      }
      setLastResult(null);
      setReviewPrompt(null);
      setWrongOptionIds([]);
      setPracticeSelectedId('');
      setPracticeResolved(false);
      setReadingConfirmed(false);
      setReadingAnswers({});
      if (!isGuest) await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Не удалось завершить.');
    } finally {
      setBusy(false);
    }
  }

  async function saveComment(problemId) {
    setCommentBusyId(problemId);
    try {
      await api.patch(`/trainers/problems/${problemId}/`, {
        teacher_comment: commentDrafts[problemId] ?? '',
      });
      await load({ quiet: true });
    } catch {
      setError('Не удалось сохранить комментарий.');
    } finally {
      setCommentBusyId(null);
    }
  }

  // Как на masobr: первый Enter — проверить ответ, второй — «Далее» / завершить.
  const onTrainerEnterRef = useRef(() => {});
  onTrainerEnterRef.current = () => {
    if (!isOwner || isFinished || busy) return;
    if (showingFeedback) {
      if (allAnswered) {
        void finish();
      } else {
        goNext();
      }
      return;
    }
    if (!current) {
      if (allAnswered) void finish();
      return;
    }
    if (isReadingRecall(current.prompt)) {
      const needConfirm = Boolean(current.prompt?.require_read_confirm);
      if (needConfirm && !readingConfirmed) {
        setReadingConfirmed(true);
        return;
      }
      if (readingAnswersReady(current.prompt, readingAnswers)) {
        void submitCurrent();
      }
      return;
    }
    const ready = current.prompt?.type === 'bit_answer'
      ? isBitAnswerReady(answer)
      : isTextAnswerType(current.prompt?.type)
        ? Boolean(answer.trim())
        : Boolean(selectedId);
    if (ready) void submitCurrent();
  };

  useEffect(() => {
    function onKeyDown(e) {
      if (e.key !== 'Enter' && e.code !== 'Enter') return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.shiftKey) return;
      const tag = e.target?.tagName;
      if (tag === 'TEXTAREA') return;
      e.preventDefault();
      onTrainerEnterRef.current();
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (loading) {
    return (
      <div className="practice-quizzes-page">
        <p className="text-secondary">Загрузка…</p>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="practice-quizzes-page">
        <div className="alert alert-error">{error || 'Сессия не найдена'}</div>
        <Link to="/trainers">К тренажёрам</Link>
      </div>
    );
  }

  const progressLabel = `${session.problems_answered} / ${session.target_count}`;
  const activeIndex = showingFeedback
    ? (lastResult?.index ?? 0)
    : (current?.index ?? 0);

  return (
    <div className="practice-quizzes-page">
      <div className="catalog-page-header">
        <p style={{ marginBottom: '0.35rem' }}>
          <Link to="/trainers">← Тренажёры</Link>
        </p>
        <h1 className="catalog-page-title">{session.kind_title || session.kind}</h1>
        <p className="catalog-page-lead">
          Прогресс: {progressLabel}
          {` · верно: ${session.problems_correct}`}
          {isFinished ? ' · завершена' : ''}
          {isGuest ? ' · результаты не сохраняются' : ''}
        </p>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {isOwner && !isFinished && showingFeedback && (
        <section className="dashboard-section" style={{ marginBottom: '1.5rem' }}>
          <h2>
            Задание {activeIndex + 1} из {session.target_count}
          </h2>
          {isReadingRecall(reviewPrompt) ? (
            <>
              <ProblemPrompt
                prompt={{
                  ...reviewPrompt,
                  _showStatement: true,
                }}
              />
              <ReadingQuestions
                prompt={reviewPrompt}
                answers={
                  typeof lastResult?.student_answer === 'object' && lastResult?.student_answer
                    ? lastResult.student_answer
                    : readingAnswers
                }
                onSelect={() => {}}
                namePrefix={`review-read-${lastResult?.id || 'x'}`}
                locked
                perQuestion={lastResult?.feedback?.per_question || null}
                showCorrect
              />
            </>
          ) : (
            <>
              <ProblemPrompt prompt={reviewPrompt} />
              {isMcqPrompt(reviewPrompt) && (
                <McqPromptOptions
                  prompt={reviewPrompt}
                  selectedId={
                    lastResult?.is_correct === true
                      ? (selectedId || String(lastResult?.student_answer || ''))
                      : practiceSelectedId
                  }
                  onSelect={lastResult?.is_correct === true ? () => {} : setPracticeSelectedId}
                  name={`review-${lastResult?.id || 'x'}`}
                  wrongIds={lastResult?.is_correct === true ? [] : wrongOptionIds}
                  correctFoundId={
                    lastResult?.is_correct === true || practiceResolved
                      ? (lastResult?.feedback?.correct_id || '')
                      : ''
                  }
                  locked={lastResult?.is_correct === true || practiceResolved}
                />
              )}
            </>
          )}
          <ImmediateFeedback result={lastResult} />
          {lastResult?.is_correct !== true && isMcqPrompt(reviewPrompt) && !isReadingRecall(reviewPrompt) && (
            practiceResolved ? (
              <div className="alert alert-success" style={{ marginTop: '1rem' }} role="status">
                <p style={{ margin: 0, fontWeight: 600 }}>Теперь верно</p>
              </div>
            ) : (
              <button
                type="button"
                className="btn btn-outline"
                style={{ marginTop: '1rem', marginRight: '0.5rem' }}
                disabled={!practiceSelectedId || wrongOptionIds.includes(practiceSelectedId)}
                onClick={checkPracticeChoice}
              >
                Проверить снова
              </button>
            )
          )}
          {allAnswered ? (
            <button
              type="button"
              className="btn btn-primary"
              style={{ marginTop: '1rem' }}
              disabled={busy}
              onClick={() => void finish()}
            >
              {busy ? '…' : 'Завершить сессию'}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              style={{ marginTop: '1rem' }}
              onClick={goNext}
            >
              Далее
            </button>
          )}
        </section>
      )}

      {isOwner && !isFinished && !showingFeedback && current && (
        <section className="dashboard-section" style={{ marginBottom: '1.5rem' }}>
          <h2>
            Задание {(current.index ?? 0) + 1} из {session.target_count}
          </h2>
          {isReadingRecall(current.prompt) ? (
            (() => {
              const needConfirm = Boolean(current.prompt?.require_read_confirm);
              const inReadPhase = needConfirm && !readingConfirmed;
              const hideText = Boolean(current.prompt?.hide_statement) && readingConfirmed;
              return (
                <>
                  <ProblemPrompt
                    prompt={{
                      ...current.prompt,
                      _showStatement: !hideText,
                    }}
                  />
                  {inReadPhase ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      style={{ marginTop: '1rem' }}
                      onClick={() => setReadingConfirmed(true)}
                    >
                      Прочитал
                    </button>
                  ) : (
                    <>
                      <ReadingQuestions
                        prompt={current.prompt}
                        answers={readingAnswers}
                        onSelect={(qid, oid) => {
                          setReadingAnswers((prev) => ({ ...prev, [qid]: oid }));
                        }}
                        namePrefix={`read-${current.id}`}
                      />
                      <button
                        type="button"
                        className="btn btn-primary"
                        style={{ marginTop: '1rem' }}
                        disabled={busy || !readingAnswersReady(current.prompt, readingAnswers)}
                        onClick={() => void submitCurrent()}
                      >
                        {busy ? '…' : 'Проверить'}
                      </button>
                    </>
                  )}
                </>
              );
            })()
          ) : (
            <>
              <ProblemPrompt prompt={current.prompt} />

              {current.prompt?.type === 'write_regex' || current.prompt?.type === 'write_logic' ? (
                <label className="form-label" style={{ display: 'block', marginTop: '1rem' }}>
                  {current.prompt?.type === 'write_logic' ? 'Выражение Python' : 'Шаблон'}
                  <input
                    type="text"
                    className="form-input"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder={
                      current.prompt?.type === 'write_logic'
                        ? '(not A or B) <= C'
                        : "например \\d+ или r'\\d+'"
                    }
                    autoComplete="off"
                    spellCheck={false}
                    autoFocus
                    style={{ fontFamily: 'ui-monospace, monospace' }}
                  />
                </label>
              ) : current.prompt?.type === 'predict_result' ? (
                <label className="form-label" style={{ display: 'block', marginTop: '1rem' }}>
                  Результат
                  <input
                    type="text"
                    className="form-input"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder={"например форм или 'форм'"}
                    autoComplete="off"
                    spellCheck={false}
                    autoFocus
                    style={{ fontFamily: 'ui-monospace, monospace' }}
                  />
                </label>
              ) : current.prompt?.type === 'write_slice' ? (
                <label className="form-label" style={{ display: 'block', marginTop: '1rem' }}>
                  Срез
                  <input
                    type="text"
                    className="form-input"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="например s[2:6] или 2:6"
                    autoComplete="off"
                    spellCheck={false}
                    autoFocus
                    style={{ fontFamily: 'ui-monospace, monospace' }}
                  />
                </label>
              ) : current.prompt?.type === 'number_answer' ? (
                <label className="form-label" style={{ display: 'block', marginTop: '1rem' }}>
                  Ответ (число)
                  <input
                    type="text"
                    inputMode="numeric"
                    className="form-input"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="0"
                    autoComplete="off"
                    autoFocus
                  />
                </label>
              ) : current.prompt?.type === 'find_vertex' ? (
                <label className="form-label" style={{ display: 'block', marginTop: '1rem' }}>
                  Название вершины (буква)
                  <input
                    type="text"
                    className="form-input"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="например Г"
                    autoComplete="off"
                    maxLength={2}
                    autoFocus
                    style={{ maxWidth: '8rem', fontSize: '1.2rem' }}
                  />
                </label>
              ) : current.prompt?.type === 'write_code' ? (
                <label className="form-label" style={{ display: 'block', marginTop: '1rem' }}>
                  Код (0 и 1)
                  <input
                    type="text"
                    className="form-input"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value.replace(/[^01]/g, ''))}
                    placeholder="например 0110"
                    autoComplete="off"
                    spellCheck={false}
                    inputMode="numeric"
                    autoFocus
                    style={{ maxWidth: '12rem', fontFamily: 'ui-monospace, monospace', fontSize: '1.2rem' }}
                  />
                </label>
              ) : current.prompt?.type === 'write_letter' ? (
                <label className="form-label" style={{ display: 'block', marginTop: '1rem' }}>
                  Буква
                  <input
                    type="text"
                    className="form-input"
                    value={answer}
                    onChange={(e) => setAnswer(e.target.value)}
                    placeholder="например Г"
                    autoComplete="off"
                    maxLength={2}
                    autoFocus
                    style={{ maxWidth: '8rem', fontSize: '1.2rem' }}
                  />
                </label>
              ) : current.prompt?.type === 'bit_answer' ? (
                <div style={{ marginTop: '1rem' }}>
                  <p className="text-secondary" style={{ marginBottom: '0.35rem' }}>
                    Ответ — 32 бита (0 и 1), бит над битом
                  </p>
                  <TrainerIpBits
                    rows={ip10DisplayRows(current.prompt)}
                    editableLabel={current.prompt.answer_label}
                    value={answer}
                    onChange={setAnswer}
                  />
                </div>
              ) : (
                <McqPromptOptions
                  prompt={current.prompt}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  name={`opt-${current.id}`}
                />
              )}

              <button
                type="button"
                className="btn btn-primary"
                style={{ marginTop: '1rem' }}
                disabled={
                  busy
                  || (
                    current.prompt?.type === 'bit_answer'
                      ? !isBitAnswerReady(answer)
                      : isTextAnswerType(current.prompt?.type)
                        ? !answer.trim()
                        : !selectedId
                  )
                }
                onClick={() => void submitCurrent()}
              >
                {busy ? '…' : 'Проверить'}
              </button>
            </>
          )}
        </section>
      )}

      {isOwner && !isFinished && !showingFeedback && !current && allAnswered && (
        <section className="dashboard-section" style={{ marginBottom: '1.5rem' }}>
          <p>Все задания отвечены.</p>
          <button
            type="button"
            className="btn btn-primary"
            disabled={busy}
            onClick={() => void finish()}
          >
            Завершить сессию
          </button>
        </section>
      )}

      {(isFinished || teacherView) && (
        <section className="dashboard-section">
          <h2>Разбор</h2>
          <ul className="practice-quizzes-list">
            {problems.map((p) => (
              <li
                key={p.id}
                className="practice-quizzes-item"
                style={{ flexDirection: 'column', alignItems: 'stretch' }}
              >
                <div>
                  <strong>#{(p.index ?? 0) + 1}</strong>
                  {' '}
                  {p.is_correct === true && <span>верно</span>}
                  {p.is_correct === false && <span>неверно</span>}
                  {p.is_correct == null && <span className="text-secondary">нет ответа</span>}
                </div>
                <ProblemPrompt
                  prompt={
                    isReadingRecall(p.prompt)
                      ? { ...p.prompt, _showStatement: true }
                      : p.prompt
                  }
                />
                {isReadingRecall(p.prompt) && (
                  <ReadingQuestions
                    prompt={p.prompt}
                    answers={
                      typeof p.student_answer === 'object' && p.student_answer
                        ? p.student_answer
                        : {}
                    }
                    onSelect={() => {}}
                    namePrefix={`done-read-${p.id}`}
                    locked
                    perQuestion={p.feedback?.per_question || null}
                    showCorrect={p.is_correct != null}
                  />
                )}
                {p.student_answer != null && p.student_answer !== '' && !isReadingRecall(p.prompt) && (
                  <p className="text-secondary">
                    Ответ:{' '}
                    <code>
                      {typeof p.student_answer === 'string'
                        ? p.student_answer
                        : JSON.stringify(p.student_answer)}
                    </code>
                  </p>
                )}
                {p.feedback?.message && (
                  <p className="text-secondary">{p.feedback.message}</p>
                )}
                {p.feedback?.explanation && !isReadingRecall(p.prompt) && (
                  <p>{p.feedback.explanation}</p>
                )}
                {p.teacher_comment && (
                  <p>
                    <strong>Комментарий:</strong> {p.teacher_comment}
                  </p>
                )}
                {teacherView && (
                  <div style={{ marginTop: '0.5rem' }}>
                    <label className="form-label">
                      Комментарий репетитора
                      <textarea
                        className="form-input"
                        rows={2}
                        value={commentDrafts[p.id] ?? p.teacher_comment ?? ''}
                        onChange={(e) =>
                          setCommentDrafts((prev) => ({ ...prev, [p.id]: e.target.value }))
                        }
                      />
                    </label>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline"
                      disabled={commentBusyId === p.id}
                      onClick={() => void saveComment(p.id)}
                    >
                      {commentBusyId === p.id ? '…' : 'Сохранить'}
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
